import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { IoChevronBack, IoClose, IoCheckmarkCircle, IoRepeat, IoTicketOutline, IoCloseOutline, IoLocationSharp } from 'react-icons/io5';
import { FaMapMarkerAlt, FaCalendarAlt, FaImage } from 'react-icons/fa';
import { ImHome } from 'react-icons/im';
import { Popup } from 'react-leaflet';
import { useData } from '../../context/DataContext/DataProvider';
import Input from '../Input/Input';
import { RecurrenceSelector } from '../RecurrenceSelector';
import { LocationSelector } from '../LocationSelector';
import SocialsHandler from '../SocialsInput/SocialsHandler';
import ImageSlider from '../ImageSlider/ImageSlider';
import TagInput from '../TagInput/TagInput';
import ArtistSelector from '../ArtistSelector/ArtistSelector';
import { formatAddressComponents } from 'lib';
import { getVenues } from '../../api/events';
import barMarker from '../../assets/map/bar-marker.png';
import './EventPostingWizard.css';

const EventPostingWizard = ({
  isOpen,
  onClose,
  onSkip,
  onDisableWizard, // Called when "don't show again" is checked
  onComplete, // Called when "continue editing" is clicked - goes to posting page
  onPostNow, // Called when "post now" is clicked - directly submits event
  isPosting = false, // Loading state for direct posting
  wizardData,
  updateWizardData,
  currentStep,
  nextStep,
  prevStep,
  goToStep,
  isFirstStep,
  isLastStep,
  isReviewStep,
  progress,
  totalSteps,
  steps,
  currentStepInfo,
  direction,
  isCurrentStepValid,
  userLocation,
}) => {
  const { globalConfig } = useData();
  const categories = globalConfig?.categories || [];
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const contentRef = useRef(null);

  // Memoized onChange callbacks to prevent unnecessary re-renders
  const handleNameChange = useCallback((val) => updateWizardData('name', val), [updateWizardData]);
  const handleCategoryChange = useCallback((val) => updateWizardData('category', val), [updateWizardData]);
  const handleTagsChange = useCallback((val) => updateWizardData('tags', val), [updateWizardData]);
  const handleDateChange = useCallback((val) => updateWizardData('date', val), [updateWizardData]);
  const handleRecurrenceChange = useCallback((val) => updateWizardData('recurrence', val), [updateWizardData]);
  const handleLocationChange = useCallback((val) => updateWizardData('location', val), [updateWizardData]);
  const handleImagesChange = useCallback((val) => updateWizardData('images', val), [updateWizardData]);
  const handleSocialsChange = useCallback((val) => updateWizardData('socials', val), [updateWizardData]);
  const handleDescriptionChange = useCallback((val) => updateWizardData('description', val), [updateWizardData]);
  const handleTicketsChange = useCallback((field, val) => updateWizardData(field, val), [updateWizardData]);
  const handleLinkedArtistsChange = useCallback((val) => updateWizardData('linked_artists', val), [updateWizardData]);
  const handleAgeRestrictionChange = useCallback((val) => updateWizardData('age_restriction', val), [updateWizardData]);

  // Track previous step for animation direction - use ref to avoid re-renders
  const prevStepRef = useRef(currentStep);
  const animationKeyRef = useRef(0);
  
  // Update animation key when step changes (triggers CSS animation via key change)
  if (prevStepRef.current !== currentStep) {
    animationKeyRef.current += 1;
    prevStepRef.current = currentStep;
  }

  if (!isOpen) return null;

  const handleNext = () => {
    // Don't proceed if current step is invalid
    if (!isCurrentStepValid()) {
      return;
    }
    
    if (isReviewStep) {
      // On review step, "next" means "continue editing"
      onComplete(wizardData);
    } else {
      nextStep();
    }
  };

  const handlePostNow = () => {
    if (onPostNow) {
      onPostNow(wizardData);
    }
  };

  const handleBack = () => {
    if (isFirstStep) {
      handleSkip();
    } else {
      prevStep();
    }
  };

  const handleSkip = () => {
    if (dontShowAgain && onDisableWizard) {
      // Disable wizard permanently (persists to user settings)
      onDisableWizard();
    }
    onSkip();
  };

  // Render step content based on current step
  const renderStepContent = () => {
    const stepId = currentStepInfo?.id;

    switch (stepId) {
      case 'title':
        return (
          <TitleStep
            value={wizardData.name}
            onChange={handleNameChange}
          />
        );
      case 'category':
        return (
          <CategoryStep
            value={wizardData.category}
            onChange={handleCategoryChange}
            categories={categories}
          />
        );
      case 'tags':
        return (
          <TagsStep
            value={wizardData.tags}
            onChange={handleTagsChange}
            selectedCategory={wizardData.category}
          />
        );
      case 'datetime':
        return (
          <DateTimeStep
            value={wizardData.date}
            onChange={handleDateChange}
          />
        );
      case 'recurrence':
        return (
          <RecurrenceStep
            value={wizardData.recurrence}
            onChange={handleRecurrenceChange}
            baseDate={wizardData.date}
          />
        );
      case 'location':
        return (
          <LocationStep
            value={wizardData.location}
            onChange={handleLocationChange}
            userLocation={userLocation}
          />
        );
      case 'description':
        return (
          <DescriptionStep
            value={wizardData.description}
            onChange={handleDescriptionChange}
          />
        );
      case 'images':
        return (
          <ImagesStep
            value={wizardData.images}
            onChange={handleImagesChange}
          />
        );
      case 'socials':
        return (
          <SocialsStep
            value={wizardData.socials}
            onChange={handleSocialsChange}
          />
        );
      case 'tickets':
        return (
          <TicketsStep
            ticketedSelected={wizardData.ticketedSelected}
            ticketed={wizardData.ticketed}
            tickets={wizardData.tickets}
            onChange={handleTicketsChange}
          />
        );
      case 'linked_artists':
        return (
          <LinkedArtistsStep
            value={wizardData.linked_artists}
            onChange={handleLinkedArtistsChange}
          />
        );
      case 'age_restriction':
        return (
          <AgeRestrictionStep
            value={wizardData.age_restriction}
            onChange={handleAgeRestrictionChange}
          />
        );
      case 'review':
        return (
          <ReviewStep
            wizardData={wizardData}
            goToStep={goToStep}
            steps={steps}
            categories={categories}
          />
        );
      default:
        return null;
    }
  };

  
  // Compute step validity once per render
  const isStepValid = isCurrentStepValid();
  // Disable next button if step is invalid (handles both required and conditionally required steps like recurrence)
  const isNextDisabled = !isStepValid;

  return (
    <div className="wizard-overlay">
      <div className="wizard-modal">
        {/* Welcome Banner - only on first step */}
        {isFirstStep && (
          <div className="wizard-welcome-banner">
            <div className="wizard-welcome-text">
              <span className="wizard-welcome-title">event posting helper</span>
              <span className="wizard-welcome-subtitle">we'll guide you through creating your event step by step</span>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="wizard-header">
          {/* Back button - only show if not on first step */}
          {!isFirstStep ? (
            <button 
              className="wizard-back-btn"
              onClick={prevStep}
              title="Go back"
            >
              <IoChevronBack size={24} />
            </button>
          ) : (
            <div className="wizard-header-spacer" />
          )}
          
          <div className="wizard-progress-container">
            <div className="wizard-progress-dots">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={`wizard-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                  onClick={() => index < currentStep && goToStep(index)}
                  title={step.label}
                />
              ))}
            </div>
            <span className="wizard-step-counter">
              step {currentStep + 1} of {totalSteps}
            </span>
          </div>

          {/* Close button - always show on top right */}
          <button 
            className="wizard-close-btn"
            onClick={handleSkip}
            title="Close wizard"
          >
            <IoClose size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="wizard-content" ref={contentRef}>
          <div 
            key={animationKeyRef.current}
            className={`wizard-step-wrapper ${direction === 'forward' ? 'slide-in-right' : 'slide-in-left'}`}
          >
            {/* Step Label - minimal, no icon */}
            <div className="wizard-step-header">
              <h2 className="wizard-step-label">{currentStepInfo?.label}</h2>
            </div>

            {/* Step Input */}
            <div className="wizard-step-input">
              {renderStepContent()}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="wizard-footer">
          <div className="wizard-footer-left">
            {!currentStepInfo?.required && !isReviewStep && (
              <button 
                className="wizard-skip-step-btn"
                onClick={nextStep}
              >
                skip
              </button>
            )}
            
            {isFirstStep && (
              <label className="wizard-dont-show-checkbox">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                />
                <span>don't show this again</span>
              </label>
            )}

            {isReviewStep && (
              <div className="wizard-review-transfer">
                <button
                  type="button"
                  className="wizard-review-transfer-link"
                  onClick={handleNext}
                  disabled={isPosting}
                >
                  add event details without wizard
                </button>
                <div className="wizard-review-transfer-help">
                  your current progress will be saved and transfer
                </div>
              </div>
            )}
          </div>
          
          {isReviewStep ? (
            <div className="wizard-review-actions">
              <button 
                className={`wizard-post-btn ${isPosting ? 'loading' : ''}`}
                onClick={handlePostNow}
                disabled={isPosting}
              >
                {isPosting ? 'posting...' : 'post now'}
              </button>
            </div>
          ) : (
            <button 
              className={`wizard-next-btn ${isNextDisabled ? 'disabled' : ''}`}
              onClick={handleNext}
              disabled={isNextDisabled}
            >
              next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============= STEP COMPONENTS =============
// All step components are memoized to prevent unnecessary re-renders

const TitleStep = React.memo(({ value, onChange }) => {
  // Memoize the input handler to prevent Input re-renders
  const handleInputChange = useCallback((e) => {
    onChange(e.target.value);
  }, [onChange]);

  return (
    <div className="wizard-title-step">
      <Input
        placeHolder="enter event name"
        value={value}
        onChange={handleInputChange}
        autoFocus
      />
    </div>
  );
});

const CategoryStep = React.memo(({ value, onChange, categories }) => {
  return (
    <div className="wizard-category-step">
      <div className="wizard-category-grid">
        {categories.map((cat) => (
          <div
            key={cat.id}
            className={`wizard-category-card ${value === cat.id ? 'selected' : ''}`}
            onClick={() => onChange(cat.id)}
            style={{
              '--cat-color': cat.activeColor,
            }}
          >
            <div 
              className="wizard-category-dot"
              style={{ backgroundColor: cat.activeColor }}
            />
            <span className="wizard-category-name">{cat.id}</span>
            {value === cat.id && (
              <IoCheckmarkCircle className="wizard-category-check" size={20} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

const TagsStep = React.memo(({ value, onChange, selectedCategory }) => {
  const handleTagAdd = useCallback((tag) => {
    onChange([...(value || []), tag]);
  }, [onChange, value]);

  const handleTagDelete = useCallback((tag) => {
    onChange((value || []).filter((item) => item !== tag));
  }, [onChange, value]);

  return (
    <div className="wizard-tags-step">
      <div className="ui-label mb-kit-8">genres / subgenres</div>
      <TagInput
        className="posting-tags-control"
        placeHolder="add tags"
        tags={value || []}
        maxTags={5}
        counterFormat="compact"
        selectedCategory={selectedCategory}
        onTagAdd={handleTagAdd}
        onTagDelete={handleTagDelete}
      />
      <div className="posting-help">
        add genre tags to help users search your event e.g., jazz, improv, offbroadway
        <div className="h-[0.3rem]" />
        <span>no spaces or special characters</span>
      </div>
    </div>
  );
});

const DateTimeStep = React.memo(({ value, onChange }) => {
  // Memoize handlers to prevent re-renders
  const handleDateChange = useCallback((e) => {
    if (e.target.value === "") return;
    onChange({ ...value, startDate: e.target.value });
  }, [onChange, value]);

  const handleStartTimeChange = useCallback((e) => {
    if (e.target.value === "") return;
    onChange({ ...value, startTime: e.target.value });
  }, [onChange, value]);

  const handleEndTimeChange = useCallback((e) => {
    if (e.target.value === "") return;
    onChange({ ...value, endTime: e.target.value });
  }, [onChange, value]);

  // Memoize today's date to prevent recalculation
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  return (
    <div className="wizard-datetime-step">
      <div className="mb-kit-24">
        <div className="ui-label mb-kit-8">start date</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
          <input
            type="date"
            value={value.startDate || ''}
            onChange={handleDateChange}
            min={today}
            className="wizard-date-input"
          />
        </div>
      </div>
      
      <div className="flex justify-between flex-1 gap-kit-16">
        <div className="flex-1">
          <div className="ui-label mb-kit-8">start</div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
            <input
              type="time"
              step={900}
              value={value.startTime || ''}
              onChange={handleStartTimeChange}
            />
          </div>
        </div>
        <div className="flex-1">
          <div className="ui-label mb-kit-8">end</div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
            <input
              type="time"
              step={900}
              value={value.endTime || ''}
              onChange={handleEndTimeChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * RecurrenceStep - Wrapper that uses the shared RecurrenceSelector component
 */
const RecurrenceStep = React.memo(({ value, onChange, baseDate }) => {
  return (
    <div className="wizard-recurrence-step">
      <RecurrenceSelector
        value={value}
        onChange={onChange}
        baseDate={baseDate}
        compact
      />
    </div>
  );
});

/**
 * LocationStep - Wrapper that uses the shared LocationSelector component
 */
const LocationStep = React.memo(({ value, onChange, userLocation }) => {
  const [venues, setVenues] = useState([]);
  const locationSelectorRef = useRef(null);

  // Fetch nearby venues when userLocation changes
  useEffect(() => {
    const fetchVenues = async () => {
      if (userLocation) {
        try {
          const venuesData = await getVenues(userLocation);
          setVenues(venuesData || []);
        } catch (error) {
          console.error('Error fetching venues:', error);
        }
      }
    };
    fetchVenues();
  }, [userLocation]);

  // Also fetch venues when location changes
  const handleLocationChange = useCallback((newLocation) => {
    onChange(newLocation);
    // Fetch nearby venues when location changes
    if (newLocation.coordinates) {
      getVenues({
        lat: newLocation.coordinates.lat,
        lng: newLocation.coordinates.lng
      }).then(venuesData => {
        setVenues(venuesData || []);
      }).catch(error => {
        console.error('Error fetching venues:', error);
      });
    }
  }, [onChange]);

  // Handle venue selection
  const handleVenuePick = useCallback((venue) => {
    onChange({
      address: venue.address || '',
      coordinates: venue.location?.coordinates ? {
        lat: venue.location.coordinates[1],
        lng: venue.location.coordinates[0]
      } : null,
      venue: venue,
      addressFields: null
    });
  }, [onChange]);

  // Handle custom marker at venue location (use location but not as venue)
  const handleMapClickAtVenue = useCallback((lng, lat) => {
    // This sets the location but doesn't select the venue
    // The AddressHandler will reverse geocode to get the address
    locationSelectorRef.current?.setState?.({
      location: {
        type: "Point",
        coordinates: [lng, lat]
      },
      venue: null
    });
  }, []);

  // Convert venues to map markers with popups
  const venuesToMarkers = useCallback((list) => {
    if (!list || list.length === 0) {
      return [];
    }
    
    const currentVenue = locationSelectorRef.current?.getVenueId?.();
    
    return list
      .filter(venue => venue._id !== currentVenue)
      .map(venue => {
        const [lng, lat] = venue.location.coordinates;
        return {
          lng,
          lat,
          icon: barMarker,
          venue: venue,
          props: {
            onVenueSelect: () => handleVenuePick(venue),
            onMapClick: () => handleMapClickAtVenue(lng, lat),
            Popup: (props) => {
              const { onClose, onMapClick, onVenueSelect } = props;
              return (
                <Popup
                  style={{
                    backgroundColor: "var(--inyc-primary-light)",
                  }}
                  className="bg-[--inyc-primary] w-full"
                  onClose={() => onClose()}
                  {...props}
                >
                  <div
                    className="bg-[--inyc-primary] w-full"
                    style={{ padding: 'var(--inyc-space-8)', minWidth: '15rem', borderRadius: 'var(--inyc-radius-xs)' }}
                  >
                    <div className="mb-kit-8">
                      <div className="text-[--inyc-primary-text-color-light] text-kit-sm">
                        venue
                      </div>
                      <div className="break-all mb-kit-4 text-[--inyc-primary-text-color] text-kit-md">
                        {venue?.name || 'Venue'}
                      </div>
                      <div className="text-kit-sm mb-kit-8 font-['Primary-Regular'] text-[--inyc-primary-text-color-light]">
                        {venue?.address}
                      </div>
                    </div>
                    <div
                      className="flex items-center justify-start cursor-pointer px-kit-12 py-kit-8 rounded-kit-xs w-full hover:text-[--inyc-primary-text-color] transition-all duration-300 hover:bg-[--inyc-primary-light] text-[--inyc-primary-text-color] text-kit-md"
                      style={{ width: '100%' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onVenueSelect();
                        onClose();
                      }}
                    >
                      <ImHome className="mr-kit-8" size={11} />
                      <div className="text-kit-md leading-[1.5rem] text-[--inyc-primary-text-color]">
                        select venue
                      </div>
                    </div>
                    <div
                      className="flex items-center justify-start cursor-pointer px-kit-12 py-kit-8 rounded-kit-xs w-full hover:text-[--inyc-primary-text-color] transition-all duration-300 hover:bg-[--inyc-primary-light] text-[--inyc-primary-text-color] text-kit-md"
                      style={{ width: '100%' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onMapClick();
                        onClose();
                      }}
                    >
                      <IoLocationSharp className="mr-kit-8" size={12} />
                      <div className="text-kit-md leading-[1.5rem] text-[--inyc-primary-text-color]">
                        add custom marker
                      </div>
                    </div>
                  </div>
                </Popup>
              );
            }
          }
        };
      });
  }, [handleVenuePick, handleMapClickAtVenue]);

  return (
    <div className="wizard-location-step">
      <LocationSelector
        ref={locationSelectorRef}
        value={value}
        onChange={handleLocationChange}
        userLocation={userLocation}
        mapMarkers={venuesToMarkers(venues)}
      />
    </div>
  );
});

const DescriptionStep = React.memo(({ value, onChange }) => {
  const handleInputChange = useCallback((e) => {
    onChange(e.target.value);
  }, [onChange]);

  return (
    <div className="wizard-description-step">
      <Input
        label="description"
        placeHolder="describe your event (optional)"
        value={value}
        onChange={handleInputChange}
        multiLine
        style={{ minHeight: '120px' }}
      />
      <div className="wizard-description-hint">
        tip: include what attendees can expect, any special guests, or unique highlights
      </div>
    </div>
  );
});

/**
 * ImagesStep - Wizard image selection step
 * 
 * Images are stored as { file, original } objects for transfer to EditEvent.
 */
const ImagesStep = React.memo(({ value, onChange }) => {
  return (
    <div className="wizard-images-step">
      <ImageSlider
        images={value}
        onChange={onChange}
        className={(hasImages) => `wizard-image-slider ${hasImages ? '' : 'wizard-image-slider--empty'}`}
        edit
        emptyHint="up to 10 images"
        showThumbnails
      />
    </div>
  );
});

/**
 * SocialsStep - Wrapper for SocialsHandler in the wizard
 */
const SocialsStep = React.memo(({ value, onChange }) => {
  return (
    <div className="wizard-socials-step">
      <SocialsHandler
        value={value}
        onChange={onChange}
      />
    </div>
  );
});

/**
 * TicketsStep - Wizard ticket editing step
 * 
 * NOTE: This component mirrors the ticket UI in EditEvent.jsx
 * Any changes to ticket editing UI in EditEvent should be replicated here.
 * Search for "WIZARD_SYNC" comments in EditEvent.jsx for related code.
 */
const TicketsStep = React.memo(({ ticketedSelected, ticketed, tickets, onChange }) => {
  // Manage tickets as array, same as EditEvent
  const [localTickets, setLocalTickets] = useState(
    tickets.length > 0 ? tickets : [{ name: '', priceType: 'priced_at', price: '$', priceMax: '', description: '', link: '' }]
  );
  const hasTicketChoice = ticketedSelected || ticketed === true;

  // Sync local tickets to parent when they change.
  // Sync everything (even partially-filled tickets) so in-progress edits like
  // a typed title aren't lost when navigating back/forward through the wizard.
  // Incomplete tickets are filtered out at submission time instead.
  useEffect(() => {
    onChange('tickets', localTickets);
  }, [localTickets]);

  const updateTicket = (index, field, value) => {
    const newTickets = [...localTickets];
    newTickets[index] = { ...newTickets[index], [field]: value };
    setLocalTickets(newTickets);
  };

  const removeTicket = (index) => {
    if (localTickets.length > 1) {
      setLocalTickets(localTickets.filter((_, i) => i !== index));
    }
  };

  const addTicket = () => {
    setLocalTickets([...localTickets, { name: '', priceType: 'priced_at', price: '$', priceMax: '', description: '', link: '' }]);
  };

  return (
    <div className="wizard-tickets-step">
      {/* Ticketing type cards - matching recurrence selector style (same as EditEvent) */}
      <div className="flex flex-col gap-kit-12 mb-kit-16">
        {/* No tickets option */}
        <div
          onClick={() => {
            onChange('ticketed', false);
            onChange('ticketedSelected', true);
            setLocalTickets([{ name: '', priceType: 'priced_at', price: '$', priceMax: '', description: '', link: '' }]);
          }}
          className={`flex items-center gap-kit-16 p-kit-16 rounded-kit-lg cursor-pointer transition-all duration-200 border-[2px] ${
            hasTicketChoice && !ticketed
              ? 'border-[var(--inyc-secondary)] bg-[var(--inyc-secondary)]/10'
              : 'border-[var(--inyc-input-border-color)] hover:border-[var(--inyc-secondary)]/50'
          }`}
        >
          <div className={`w-[3rem] h-[3rem] rounded-kit-full flex items-center justify-center ${
            hasTicketChoice && !ticketed ? 'bg-[var(--inyc-secondary)]' : 'bg-[var(--inyc-primary-light)]'
          }`}>
            <IoCloseOutline size={24} className={hasTicketChoice && !ticketed ? 'text-[var(--inyc-secondary-text-color)]' : 'opacity-60'} />
          </div>
          <div className="flex-1">
            <div className="font-['Primary-Bold'] text-[var(--inyc-primary-text-color)]">no tickets</div>
          </div>
          {hasTicketChoice && !ticketed && <IoCheckmarkCircle size={24} className="text-[var(--inyc-secondary)]" />}
        </div>

        {/* Tickets / donation / entry fee option */}
        <div
          onClick={() => {
            onChange('ticketed', true);
            onChange('ticketedSelected', true);
            if (localTickets.length === 0) {
              setLocalTickets([{ name: '', priceType: 'priced_at', price: '$', priceMax: '', description: '', link: '' }]);
            }
          }}
          className={`flex items-center gap-kit-16 p-kit-16 rounded-kit-lg cursor-pointer transition-all duration-200 border-[2px] ${
            hasTicketChoice && ticketed
              ? 'border-[var(--inyc-secondary)] bg-[var(--inyc-secondary)]/10'
              : 'border-[var(--inyc-input-border-color)] hover:border-[var(--inyc-secondary)]/50'
          }`}
        >
          <div className={`w-[3rem] h-[3rem] rounded-kit-full flex items-center justify-center ${
            hasTicketChoice && ticketed ? 'bg-[var(--inyc-secondary)]' : 'bg-[var(--inyc-primary-light)]'
          }`}>
            <IoTicketOutline size={24} className={hasTicketChoice && ticketed ? 'text-[var(--inyc-secondary-text-color)]' : 'opacity-60'} />
          </div>
          <div className="flex-1">
            <div className="font-['Primary-Bold'] text-[var(--inyc-primary-text-color)]">tickets / donation / entry fee</div>
            <div className="text-kit-base font-['Primary-Medium'] text-[var(--inyc-primary-text-color)] opacity-75">add ticket tiers with prices and links</div>
          </div>
          {hasTicketChoice && ticketed && <IoCheckmarkCircle size={24} className="text-[var(--inyc-secondary)]" />}
        </div>
      </div>

      {ticketed && (
        <div className="pt-kit-24 flex flex-col gap-kit-16">
          {/* Ticket cards - exact same structure as EditEvent - WIZARD_SYNC */}
          {localTickets.map((ticket, index) => (
            <div 
              key={index} 
              className="relative flex flex-col gap-kit-16 pt-kit-32 pb-kit-20 px-kit-20 rounded-kit-md border-[1px] border-[var(--inyc-input-border-color)]"
              style={{ backgroundColor: 'var(--inyc-card-background-color, rgba(0,0,0,0.02))' }}
            >
              {/* X button only for multiple tiers */}
              {localTickets.length > 1 && (
                <div
                  onClick={() => removeTicket(index)}
                  className="absolute top-[0.6rem] right-[0.6rem] cursor-pointer p-kit-4 rounded-kit-full opacity-30 hover:opacity-100 duration-200"
                  title="remove"
                >
                  <IoClose size={18} />
                </div>
              )}
              
              {/* Row 1: Name + Price type pills inline - mt accounts for floating label */}
              <div className="flex items-end gap-kit-16 flex-wrap mt-kit-8">
                <div className="flex-1 min-w-[180px]">
                  <Input
                    label={`tier ${index + 1} name`}
                    placeHolder="e.g., general admission"
                    value={ticket.name}
                    onChange={(e) => updateTicket(index, 'name', e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="flex gap-kit-4 pb-kit-8 mb-kit-8">
                  {[
                    { value: 'priced_at', label: 'priced at' },
                    { value: 'starting_at', label: 'starting at' },
                    { value: 'range', label: 'range' },
                    { value: 'donation', label: 'donation' },
                    { value: 'rsvp', label: 'rsvp' },
                    { value: 'door', label: 'door' },
                  ].map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        const newTickets = [...localTickets];
                        newTickets[index].priceType = option.value;
                        if (option.value !== 'range') newTickets[index].priceMax = "";
                        setLocalTickets(newTickets);
                      }}
                      className={`cursor-pointer px-kit-12 py-kit-6 rounded-kit-sm text-kit-base font-['Primary-Medium'] duration-200 ${
                        ticket.priceType === option.value
                          ? 'bg-[var(--inyc-primary-text-color)] text-[var(--inyc-primary)]'
                          : 'text-[var(--inyc-primary-text-color)] opacity-70 hover:opacity-100'
                      }`}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Row 2: Price + Link */}
              <div className="flex flex-col sm:flex-row sm:items-end gap-kit-24 sm:gap-kit-12 mt-kit-12">
                <div className="flex items-end gap-kit-12">
                    <Input
                      label={ticket.priceType === 'range' ? 'min' : 'price'}
                      placeHolder="$0"
                      value={ticket.price}
                      onChange={(e) => {
                        let value = e.target.value;
                        const allowed = /^\$[0-9]*(\.[0-9]{0,3})?$/;
                        if (value && value[0] !== "$") value = `$${value}`;
                        if (value === "$" || allowed.test(value)) {
                          updateTicket(index, 'price', value);
                        }
                      }}
                      style={{ width: '120px' }}
                    />
                  {ticket.priceType === 'range' && (
                    <>
                      <span className="text-[var(--inyc-primary-text-color)] opacity-70 pb-kit-10">–</span>
                      <Input
                        label="max"
                        placeHolder="$0"
                        value={ticket.priceMax}
                        onChange={(e) => {
                          let value = e.target.value;
                          const allowed = /^\$[0-9]*(\.[0-9]{0,3})?$/;
                          if (value && value[0] !== "$") value = `$${value}`;
                          if (value === "$" || value === "" || allowed.test(value)) {
                            updateTicket(index, 'priceMax', value);
                          }
                        }}
                        style={{ width: '120px' }}
                      />
                    </>
                  )}
                </div>
                <Input
                  label="ticket or payment link"
                  placeHolder="venmo, paypal, ticket link..."
                  value={ticket.link}
                  onChange={(e) => updateTicket(index, 'link', e.target.value)}
                  className="w-full sm:flex-1 sm:min-w-[220px]"
                />
              </div>
            </div>
          ))}
          
          {/* Add ticket tier button - same as EditEvent */}
          <button
            onClick={addTicket}
            className="py-kit-12 rounded-kit-md border-[2px] border-dashed border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color)] opacity-50 hover:opacity-100 duration-200 font-['Primary-Medium']"
          >
            + add ticket tier
          </button>
        </div>
      )}
    </div>
  );
});

const LinkedArtistsStep = React.memo(({ value, onChange }) => {
  return (
    <div className="wizard-linked-artists-step">
      <ArtistSelector
        selectedArtists={value || []}
        onChange={onChange}
        maxArtists={10}
        label="tag artists"
      />
    </div>
  );
});

const AgeRestrictionStep = React.memo(({ value, onChange }) => {
  const handleInputChange = useCallback((e) => {
    onChange(e.target.value);
  }, [onChange]);

  return (
    <div className="wizard-age-step">
      <Input
        label="minimum age"
        placeHolder="(optional)"
        value={value}
        onChange={handleInputChange}
      />
    </div>
  );
});

/**
 * ReviewStep - Shows a preview of the event as it will appear in the app
 * Uses a simplified version of EventDetails styling
 * Step indices follow WIZARD_STEPS in useEventWizard.js
 */
const ReviewStep = React.memo(({ wizardData, goToStep, steps, categories }) => {
  // Parse date string as local date to avoid timezone issues
  const parseDateAsLocal = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };
  
  const formatDate = (date) => {
    if (!date.startDate) return null;
    const d = parseDateAsLocal(date.startDate);
    if (!d) return null;
    return d.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'pm' : 'am';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes}${ampm}`;
  };

  const formatRecurrence = () => {
    const { recurrence } = wizardData;
    if (recurrence.type === 'single') return null;
    if (recurrence.type === 'repeating') {
      const num = recurrence.repeatEveryNumber;
      const period = recurrence.repeatEveryPeriod;
      const periodLabel = num > 1 ? `${period}s` : period;
      let text = `every ${num > 1 ? num + ' ' : ''}${periodLabel}`;
      if (recurrence.repeatEndsOn) {
        const endDate = new Date(recurrence.repeatEndsOn);
        text += ` until ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      }
      return text;
    }
    if (recurrence.type === 'multiple' && recurrence.extraDates.length > 0) {
      return `+ ${recurrence.extraDates.length} more date${recurrence.extraDates.length > 1 ? 's' : ''}`;
    }
    return null;
  };

  const category = categories.find(c => c.id === wizardData.category);
  const hasImages = wizardData.images && wizardData.images.length > 0;
  const recurrenceText = formatRecurrence();
  const [reviewImageRatio, setReviewImageRatio] = useState(null);
  const firstImage = wizardData.images?.[0];
  const reviewImage = useMemo(() => {
    if (!firstImage) return null;
    if (firstImage.original || firstImage.base64) {
      return { src: firstImage.original || firstImage.base64, revoke: false };
    }
    if (typeof File !== 'undefined' && firstImage instanceof File) {
      return { src: URL.createObjectURL(firstImage), revoke: true };
    }
    if (typeof firstImage === 'string') {
      return { src: firstImage, revoke: false };
    }
    return null;
  }, [firstImage]);

  useEffect(() => {
    setReviewImageRatio(null);
    return () => {
      if (reviewImage?.revoke) {
        URL.revokeObjectURL(reviewImage.src);
      }
    };
  }, [reviewImage?.src, reviewImage?.revoke]);

  const goToStepId = (id) => {
    const index = steps.findIndex((step) => step.id === id);
    if (index >= 0) goToStep(index);
  };

  return (
    <div className="wizard-review-step">
      {/* Note at the top */}
      <div className="text-kit-base text-[var(--inyc-primary-text-color)] opacity-70 text-center mb-kit-16 px-kit-8">
        review your event details before posting
      </div>
      
      {/* Event Preview Card - mimics EventDetails styling */}
      <div className="rounded-kit-lg border border-[var(--inyc-input-border-color)] overflow-hidden bg-[var(--inyc-primary)]">
        {/* Image Section */}
        {hasImages ? (
          <div
            className="wizard-review-image-frame"
            style={reviewImageRatio ? { aspectRatio: reviewImageRatio } : undefined}
          >
            {reviewImage?.src && (
              <img
                src={reviewImage.src}
                alt="Event"
                className="w-full h-full object-contain"
                onLoad={(event) => {
                  const { naturalWidth, naturalHeight } = event.currentTarget;
                  if (naturalWidth && naturalHeight) {
                    setReviewImageRatio(`${naturalWidth} / ${naturalHeight}`);
                  }
                }}
              />
            )}
          </div>
        ) : (
          <div className="w-full h-[100px] bg-[var(--inyc-primary-light)] flex items-center justify-center">
            <FaImage size={32} className="opacity-20" />
          </div>
        )}
        
        {/* Content Section */}
        <div className="p-kit-16">
          {/* Category pill */}
          {category && (
            <div 
              className="inline-flex items-center gap-kit-6 px-kit-10 py-kit-4 rounded-kit-lg text-kit-sm font-['Primary-Medium'] mb-kit-8 cursor-pointer hover:opacity-70"
              onClick={() => goToStepId('category')}
              style={{ 
                backgroundColor: `${category.activeColor}20`,
                color: category.activeColor
              }}
            >
              <span 
                className="w-[6px] h-[6px] rounded-kit-full"
                style={{ backgroundColor: category.activeColor }}
              />
              {category.id}
            </div>
          )}
          
          {/* Title */}
          <h2 
            className="text-kit-lg font-['Primary-Bold'] text-[var(--inyc-primary-text-color)] mb-kit-8 cursor-pointer hover:underline"
            onClick={() => goToStepId('title')}
          >
            {wizardData.name || <span className="opacity-40 italic">no title</span>}
          </h2>
          
          {/* Date & Time */}
          <div 
            className="flex items-center gap-kit-8 text-[var(--inyc-primary-text-color)] mb-kit-4 cursor-pointer hover:opacity-70"
            onClick={() => goToStepId('datetime')}
          >
            <FaCalendarAlt size={14} className="opacity-60" />
            <span className="text-kit-base">
              {formatDate(wizardData.date) || <span className="opacity-40 italic">no date</span>}
              {wizardData.date.startTime && wizardData.date.endTime && (
                <span className="opacity-70 ml-kit-8">
                  {formatTime(wizardData.date.startTime)} - {formatTime(wizardData.date.endTime)}
                </span>
              )}
            </span>
          </div>
          
          {/* Recurrence info */}
          {recurrenceText && (
            <div 
              className="flex items-center gap-kit-8 text-[var(--inyc-primary-text-color)] mb-kit-6 cursor-pointer hover:opacity-70 ml-kit-24"
              onClick={() => goToStepId('recurrence')}
            >
              <IoRepeat size={14} className="opacity-60" />
              <span className="text-kit-sm text-[var(--inyc-secondary)]">
                {recurrenceText}
              </span>
            </div>
          )}
          
          {/* Location */}
          <div 
            className="flex items-center gap-kit-8 text-[var(--inyc-primary-text-color)] mb-kit-12 cursor-pointer hover:opacity-70"
            onClick={() => goToStepId('location')}
          >
            <FaMapMarkerAlt size={14} className="opacity-60" />
            <span className="text-kit-base">
              {wizardData.location.addressFields 
                ? formatAddressComponents(wizardData.location.addressFields)
                : wizardData.location.address || <span className="opacity-40 italic">no location</span>}
            </span>
          </div>

          {wizardData.tags?.length > 0 && (
            <div
              className="mb-kit-12 flex flex-wrap gap-kit-6 cursor-pointer"
              onClick={() => goToStepId('tags')}
            >
              {wizardData.tags.map((tag) => (
                <span key={tag} className="rounded-kit-full bg-[var(--inyc-primary-light)] px-kit-10 py-kit-4 text-kit-sm">
                  {tag}
                </span>
              ))}
            </div>
          )}
          
          {/* Description */}
          {wizardData.description && (
            <p 
              className="text-kit-base text-[var(--inyc-primary-text-color)] opacity-80 mb-kit-12 line-clamp-3 cursor-pointer hover:opacity-60"
              onClick={() => goToStepId('description')}
            >
              {wizardData.description}
            </p>
          )}
          
          {/* Tickets */}
          {wizardData.ticketed && wizardData.tickets.length > 0 && (
            <div 
              className="flex flex-wrap gap-kit-8 cursor-pointer"
              onClick={() => goToStepId('tickets')}
            >
              {wizardData.tickets.map((ticket, i) => (
                <div 
                  key={i}
                  className="px-kit-12 py-kit-6 rounded-kit-sm bg-[var(--inyc-primary-light)] text-kit-sm"
                >
                  <span className="font-['Primary-Medium']">{ticket.name}</span>
                  {ticket.price && ticket.price !== '$' && (
                    <span className="ml-kit-6 opacity-70">{ticket.price}</span>
                  )}
                  {ticket.priceType === 'donation' && (!ticket.price || ticket.price === '$') && (
                    <span className="ml-kit-6 opacity-70">donation</span>
                  )}
                  {ticket.priceType === 'rsvp' && (!ticket.price || ticket.price === '$') && (
                    <span className="ml-kit-6 opacity-70">rsvp</span>
                  )}
                  {ticket.priceType === 'door' && (!ticket.price || ticket.price === '$') && (
                    <span className="ml-kit-6 opacity-70">door</span>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {!wizardData.ticketed && (
            <div 
              className="text-kit-sm text-[var(--inyc-primary-text-color)] opacity-60 cursor-pointer hover:opacity-100"
              onClick={() => goToStepId('tickets')}
            >
              free event
            </div>
          )}

          {wizardData.linked_artists?.length > 0 && (
            <div
              className="mt-kit-12 text-kit-sm text-[var(--inyc-primary-text-color)] opacity-75 cursor-pointer hover:opacity-100"
              onClick={() => goToStepId('linked_artists')}
            >
              artists: {wizardData.linked_artists.map((artist) => artist.username || artist.name || artist.businessName).filter(Boolean).join(', ')}
            </div>
          )}

          {wizardData.age_restriction && (
            <div
              className="mt-kit-6 text-kit-sm text-[var(--inyc-primary-text-color)] opacity-75 cursor-pointer hover:opacity-100"
              onClick={() => goToStepId('age_restriction')}
            >
              minimum age: {wizardData.age_restriction}
            </div>
          )}
        </div>
      </div>
      
      {/* Tap to edit hint */}
      <div className="text-kit-sm text-[var(--inyc-primary-text-color)] opacity-50 text-center mt-kit-12">
        tap any section above to edit
      </div>
    </div>
  );
});

export default EventPostingWizard;

